import { Step } from 'react-joyride';
import { tourSteps } from './tourSteps';

export type TourStepName =
  | 'search-input' | 'ask-ai-button' | 'suggestion-wizard' | 'search-controls'
  | 'type-dropdown' | 'results-button' | 'results-dropdown' | 'csv-upload'
  | 'try-search' | 'query-processing' | 'recent-datasets' | 'lead-table'
  | 'table-criteria' | 'match-cell' | 'match-popover'
  | 'chat-panel' | 'workflow-buttons' | 'navigate-home'
  | 'mobile-open-menu' | 'sidebar-overview' | 'sidebar-datasets'
  | 'settings-button'  | 'settings-modal' | 'tour-complete' | 'mobile-exit-detail';

export type TourSection = 'discovery' | 'dataset' | 'navigation' | 'settings';

export const TOUR_SECTIONS: Record<TourSection, { label: string; steps: TourStepName[] }> = {
  discovery: {
    label: 'Discovery',
    steps: ['search-input', 'ask-ai-button', 'suggestion-wizard', 'search-controls', 'type-dropdown', 'results-button', 'results-dropdown', 'csv-upload', 'try-search', 'query-processing', 'recent-datasets'],
  },
  dataset: {
    label: 'Working with Leads',
    steps: ['lead-table', 'table-criteria', 'match-cell', 'match-popover', 'chat-panel', 'workflow-buttons'],
  },
  navigation: {
    label: 'Navigation',
    steps: ['navigate-home', 'mobile-open-menu', 'sidebar-overview', 'sidebar-datasets'],
  },
  settings: {
    label: 'Settings',
    steps: ['settings-button', 'settings-modal', 'tour-complete'],
  },
};

export interface TourStep extends Step {
  name: TourStepName;
  page: 'dashboard' | 'dataset';
  requiresClick?: boolean;
  clickPrompt?: string;
  sampleQuery?: string;
  mobileTarget?: string;
  mobileTitle?: string;
  mobileContent?: string;
  mobilePlacement?: Step['placement'];
  mobileRequiresClick?: boolean;
  mobileClickPrompt?: string;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
}

// Step indices based on actual tourSteps array
// Discovery: 0-10, Dataset: 11-17, Navigation: 18-21, Settings: 22-24
export const STEP_INDICES = {
  DASHBOARD_START: 0,
  TRY_SEARCH: 8,
  QUERY_PROCESSING: 9,
  RECENT_DATASETS: 10,
  DATASET_START: 11,
  DATASET_END: 17,        // workflow-buttons is last dataset step
  NAVIGATE_HOME: 18,      // Special transition step (starts on dataset, ends on dashboard)
  SIDEBAR_START: 18,
  SIDEBAR_END: 21,
  SETTINGS_START: 22,
  TOUR_COMPLETE: 24,
};

export const TOUR_SAMPLE_DATA = {
  datasetName: 'YC AI Startups',
  datasetId: 'tour-sample-dataset',
};

export const getPageForStep = (stepIndex: number): 'dashboard' | 'dataset' | 'transition' => {
  if (stepIndex <= STEP_INDICES.RECENT_DATASETS) return 'dashboard';
  if (stepIndex <= STEP_INDICES.DATASET_END) return 'dataset';
  // navigate-home is a transition step - don't force redirect
  if (stepIndex === STEP_INDICES.NAVIGATE_HOME) return 'transition';
  return 'dashboard';
};

export const getStepSectionInfo = (stepName: TourStepName) => {
  for (const [key, section] of Object.entries(TOUR_SECTIONS)) {
    const idx = section.steps.indexOf(stepName);
    if (idx !== -1) {
      return { section: key as TourSection, label: section.label, stepInSection: idx + 1, totalInSection: section.steps.length };
    }
  }
  return null;
};

export const getSectionStartIndex = (section: TourSection): number => {
  const firstStep = TOUR_SECTIONS[section].steps[0];
  return tourSteps.findIndex(s => s.name === firstStep);
};

// Tour steps are defined in a separate file to keep this under 100 lines
export { tourSteps } from './tourSteps';
